import React, { useState, useEffect, useCallback, useRef } from "react";
import { Form, Modal } from "react-bootstrap";
import { Toaster, toast } from "react-hot-toast";

import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { parseApiPagination } from "../utils/parseApiPagination";
import {
  postUrdLedger,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../erpClient";
import { generateUrdLedgerPdfBlob } from "../utils/generateUrdLedgerPdf";

function toDateOnlyTimestamp(value) {
  if (!value) return null;
  const str = String(value).trim().split(" ")[0];
  let year;
  let month;
  let day;

  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    [year, month, day] = str.split("-").map(Number);
  } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const parts = str.split("/");
    day = Number(parts[0]);
    month = Number(parts[1]);
    year = Number(parts[2]);
  } else if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const parts = str.split("-");
    day = Number(parts[0]);
    month = Number(parts[1]);
    year = Number(parts[2]);
  } else {
    const parsed = new Date(str);
    if (Number.isNaN(parsed.getTime())) return null;
    year = parsed.getFullYear();
    month = parsed.getMonth() + 1;
    day = parsed.getDate();
  }

  return new Date(year, month - 1, day).getTime();
}

function processUrdLedgerEntries(entries) {
  return entries.map((entry) => {
    const rawDate =
      entry.date || entry.purchase_date || entry.bill_date || "";

    // Format date from YYYY-MM-DD to DD/MM/YYYY
    let formattedDate = rawDate || "-";
    if (formattedDate !== "-" && formattedDate.includes("-")) {
      const dateParts = formattedDate.split(" ")[0].split("-");
      if (dateParts.length === 3 && dateParts[0].length === 4) {
        formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
      }
    }

    // Format name: company_name (name) if name exists, otherwise just company_name
    let displayName = entry.company_name || entry.name || "-";
    if (entry.name && entry.name.trim() && entry.company_name) {
      displayName = `${entry.company_name} (${entry.name})`;
    } else if (entry.company_name) {
      displayName = entry.company_name;
    } else if (entry.name) {
      displayName = entry.name;
    }

    return {
      name: displayName,
      phone: entry.contact_number || entry.phone || entry.mobile || "-",
      purchase_no: entry.purchase_no || entry.voucher_number || "-",
      purchase_date: formattedDate,
      purchase_value: parseFloat(
        entry.total_amount || entry.grand_total || entry.total_amt || 0
      ),
      taxable_value: parseFloat(
        entry.gross_amount ||
        entry.final_amount ||
        entry.taxable_value ||
        entry.total_amt ||
        0
      ),
      sgst: parseFloat(entry.sgst || 0),
      cgst: parseFloat(entry.cgst || 0),
      total_tax: parseFloat(
        entry.gst_amount || entry.total_tax || entry.gst || 0
      ),
      gold_gm: parseFloat(
        entry.gold_gm || entry.gold_weight || entry.total_fine || 0
      ),
      silver_gm: parseFloat(entry.silver_gm || entry.silver_weight || 0),
    };
  });
}

function summarizeUrdLedgerRows(rows) {
  return rows.reduce(
    (acc, entry) => {
      acc.purchase_value += entry.purchase_value || 0;
      acc.taxable_value += entry.taxable_value || 0;
      acc.sgst += entry.sgst || 0;
      acc.cgst += entry.cgst || 0;
      acc.total_tax += entry.total_tax || 0;
      acc.gold_gm += entry.gold_gm || 0;
      acc.silver_gm += entry.silver_gm || 0;
      return acc;
    },
    {
      purchase_value: 0,
      taxable_value: 0,
      sgst: 0,
      cgst: 0,
      total_tax: 0,
      gold_gm: 0,
      silver_gm: 0,
    }
  );
}

function UrdLedger() {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entireReport, setEntireReport] = useState(false);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const pdfUrlRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const ignoreNextPageFetchRef = useRef(false);
  const pageCacheRef = useRef(new Map());

  // Clean up blob url on unmount
  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, []);

  const fetchUrdLedger = useCallback(
    async ({
      requireDateRange = false,
      page = 1,
      limit = 10,
      silent = false,
      bypassCache = false,
    } = {}) => {
      const cacheKey = entireReport ? `${page}:${limit}` : "";
      if (cacheKey && !bypassCache) {
        const cached = pageCacheRef.current.get(cacheKey);
        if (cached) {
          setLedgerRows(cached.ledgerRows);
          setSummaryData(cached.summaryData);
          setCurrentPage(cached.currentPage);
          setTotalCount(cached.totalCount);
          setHasMorePages(cached.hasMorePages);
          setLoading(false);
          return;
        }
      }

      if (!entireReport) {
        if (requireDateRange && (!fromDate || !toDate)) {
          toast.error("Please select both From Date and To Date.");
          return;
        }
        if (fromDate && toDate) {
          const fromMs = toDateOnlyTimestamp(fromDate);
          const toMs = toDateOnlyTimestamp(toDate);
          if (fromMs != null && toMs != null && fromMs > toMs) {
            toast.error("From Date cannot be after To Date.");
            return;
          }
        }
      }

      setLoading(true);

      const apiPayload = {};

      if (entireReport) {
        apiPayload.type = "entire";
        apiPayload.page = page;
        apiPayload.limit = limit;
      } else {
        if (fromDate) apiPayload.from_date = fromDate;
        if (toDate) apiPayload.to_date = toDate;
      }

      try {
        const payload = await postUrdLedger(apiPayload);

        if (!payload || (!isErpApiSuccess(payload) && payload.message !== "radha")) {
          toast.error(
            payload?.message || "Failed to fetch ledger data. Please try again."
          );
          setLedgerRows([]);
          setSummaryData(null);
          setTotalCount(0);
          return;
        }

        const entries = payload.data?.entries || payload.data || [];

        if (!Array.isArray(entries) || entries.length === 0) {
          toast.error("No ledger entries found for the selected date range.");
          setLedgerRows([]);
          setSummaryData(null);
          setTotalCount(0);
          return;
        }

        let processedEntries = processUrdLedgerEntries(entries);

        if (!entireReport && fromDate && toDate) {
          const fromMs = toDateOnlyTimestamp(fromDate);
          const toMs = toDateOnlyTimestamp(toDate);
          processedEntries = processedEntries.filter((entry) => {
            const dateMs = toDateOnlyTimestamp(entry.purchase_date);
            if (dateMs == null) return false;
            if (fromMs != null && dateMs < fromMs) return false;
            if (toMs != null && dateMs > toMs) return false;
            return true;
          });
        }

        if (processedEntries.length === 0) {
          toast.error("No ledger entries found for the selected date range.");
          setLedgerRows([]);
          setSummaryData(null);
          setTotalCount(0);
          return;
        }

        const summary = summarizeUrdLedgerRows(processedEntries);

        setLedgerRows(processedEntries);
        setSummaryData(summary);

        const paginationMeta = entireReport
          ? parseApiPagination(payload, processedEntries.length)
          : null;

        if (entireReport && paginationMeta) {
          setCurrentPage(paginationMeta.page);
          setTotalCount(paginationMeta.total);
          setHasMorePages(paginationMeta.hasMore);
          if (paginationMeta.limit) setItemsPerPage(paginationMeta.limit);
        } else {
          setCurrentPage(1);
          setTotalCount(processedEntries.length);
          setHasMorePages(false);
        }

        if (cacheKey) {
          pageCacheRef.current.set(cacheKey, {
            ledgerRows: processedEntries,
            summaryData: summary,
            currentPage: paginationMeta?.page || page,
            totalCount: paginationMeta?.total ?? processedEntries.length,
            hasMorePages: Boolean(paginationMeta?.hasMore),
          });
        }

        if (!silent) toast.success("Ledger generated successfully!");
      } catch (error) {
        toast.error(
          getErpRequestErrorMessage(
            error,
            "An error occurred while fetching the ledger data."
          )
        );
        console.error("Error fetching URD ledger:", error);
        setLedgerRows([]);
        setSummaryData(null);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [entireReport, fromDate, toDate]
  );

  const fetchRef = useRef(fetchUrdLedger);
  fetchRef.current = fetchUrdLedger;

  const initialFetchRef = useRef(false);
  // Fetch data only once on component mount
  useEffect(() => {
    if (initialFetchRef.current) return;
    initialFetchRef.current = true;
    fetchRef.current({ page: 1, limit: itemsPerPage });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in UrdLedger, refetching ledger...', event.detail);
      pageCacheRef.current.clear();
      fetchRef.current({
        page: currentPage,
        limit: itemsPerPage,
        silent: true,
        bypassCache: true,
      });
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);

    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [currentPage, itemsPerPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    pageCacheRef.current.clear();
    setCurrentPage(1);
    await fetchUrdLedger({
      requireDateRange: !entireReport,
      page: 1,
      limit: itemsPerPage,
      bypassCache: true,
    });
  };

  const handlePageChange = (page) => {
    if (ignoreNextPageFetchRef.current) {
      ignoreNextPageFetchRef.current = false;
      return;
    }
    setCurrentPage(page);
    fetchUrdLedger({ requireDateRange: false, page, limit: itemsPerPage, silent: true });
  };

  const handleItemsPerPageChange = (limit) => {
    ignoreNextPageFetchRef.current = true;
    pageCacheRef.current.clear();
    setItemsPerPage(limit);
    setCurrentPage(1);
    fetchUrdLedger({
      requireDateRange: false,
      page: 1,
      limit,
      silent: true,
      bypassCache: true,
    });
  };

  const handlePrint = async () => {
    if (!ledgerRows.length && totalCount === 0) {
      toast.error("Please generate the ledger first.");
      return;
    }
    if (printLoading) return;

    setPrintLoading(true);

    const printPayload = {
      page: 0,
      limit: 0,
    };

    if (entireReport) {
      printPayload.type = "entire";
      printPayload.filter_type = "entire";
    } else {
      if (fromDate) printPayload.from_date = fromDate;
      if (toDate) printPayload.to_date = toDate;
    }

    try {
      const payload = await postUrdLedger(printPayload);

      if (
        !payload ||
        (!isErpApiSuccess(payload) && payload.message !== "radha")
      ) {
        toast.error(
          payload?.message || "Failed to fetch ledger data for printing. Please try again."
        );
        return;
      }

      const entries = payload.data?.entries || payload.data || [];

      if (!Array.isArray(entries) || entries.length === 0) {
        toast.error("No ledger entries found to print.");
        return;
      }

      let processedEntries = processUrdLedgerEntries(entries);

      if (!entireReport && fromDate && toDate) {
        const fromMs = toDateOnlyTimestamp(fromDate);
        const toMs = toDateOnlyTimestamp(toDate);
        processedEntries = processedEntries.filter((entry) => {
          const dateMs = toDateOnlyTimestamp(entry.purchase_date);
          if (dateMs == null) return false;
          if (fromMs != null && dateMs < fromMs) return false;
          if (toMs != null && dateMs > toMs) return false;
          return true;
        });
      }

      const summary = summarizeUrdLedgerRows(processedEntries);

      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }

      const blob = await generateUrdLedgerPdfBlob({
        ledgerData: processedEntries,
        summaryData: summary,
      });

      const blobUrl = URL.createObjectURL(blob);
      pdfUrlRef.current = blobUrl;
      setPdfBlobUrl(blobUrl);
      setShowPDFModal(true);
    } catch (error) {
      toast.error(
        getErpRequestErrorMessage(
          error,
          "An error occurred while generating the print data."
        )
      );
      console.error("Error generating URD ledger for print:", error);
    } finally {
      setPrintLoading(false);
    }
  };

  // Format currency with commas
  const formatCurrency = (value) => {
    if (!value || value === 0) return "-";
    return `₹ ${Math.round(value).toLocaleString("en-IN")}`;
  };

  // Format currency with decimals
  const formatCurrencyDecimal = (value) => {
    if (!value || value === 0) return "-";
    return `₹ ${value.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Format weight with 3 decimal places
  const formatWeight = (value) => {
    if (!value || value === 0) return "-";
    return value.toFixed(3);
  };

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className="card">
          <div className="border-bottom title-part-padding">
            <h4 className="card-title mb-0">URD Ledger</h4>
          </div>
          <div className="card-body">
            <form noValidate="" onSubmit={handleSubmit}>
              <div className="row align-items-end">
                {/* From Date */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      setEntireReport(false);
                    }}
                    disabled={entireReport}
                  />
                </div>
                {/* To Date */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={toDate}
                    onChange={(e) => {
                      setToDate(e.target.value);
                      setEntireReport(false);
                    }}
                    disabled={entireReport}
                  />
                </div>
                {/* Entire Report Checkbox */}
                <div
                  className="mb-3 col-md-3 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <Form.Check
                    type="checkbox"
                    id="entireReportCheckbox"
                    label="Entire Report"
                    checked={entireReport}
                    onChange={(e) => {
                      setEntireReport(e.target.checked);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                {/* Submit Button */}
                <div
                  className="mb-3 col-md-3 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <button
                    type="submit"
                    className={`btn btn-primary w-100 ${buttonClass}`}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Loading...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </form>
            {/* Ledger Table */}
            {(ledgerRows.length > 0 || (entireReport && totalCount > 0)) && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    URD Purchase
                    {entireReport && totalCount > 0 ? (
                      <span className="text-muted fs-6 ms-2">
                        (Page {currentPage} of{" "}
                        {Math.max(1, Math.ceil(totalCount / itemsPerPage))})
                      </span>
                    ) : null}
                  </h5>
                  <button
                    type="button"
                    className={`btn btn-secondary ${buttonClass}`}
                    onClick={handlePrint}
                    disabled={printLoading || loading}
                  >
                    {printLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Fetching records...
                      </>
                    ) : (
                      "Print Report"
                    )}
                  </button>
                </div>
                <div className={`ledger-report-wrap mb-4 ${cardClass}`}>
                  <div className="table-responsive">
                    <table className="table text-nowrap mb-0 align-middle ledger-report-table">
                      <thead>
                        <tr>
                          <th colSpan="2" className="text-center">
                            Customer Details
                          </th>
                          <th colSpan="3" className="text-center">
                            PURCHASE DETAILS
                          </th>
                          <th rowSpan="2" className="text-end text-nowrap">
                            TAXABLE VALUE
                          </th>
                          <th colSpan="3" className="text-center">
                            AMOUNT OF TAX
                          </th>
                          <th colSpan="2" className="text-center">
                            WEIGHT
                          </th>
                        </tr>
                        <tr>
                          <th className="text-nowrap">NAME</th>
                          <th className="text-nowrap">Phone</th>
                          <th className="text-nowrap">PURCHASE NO</th>
                          <th className="text-nowrap">PURCHASE DATE</th>
                          <th className="text-end text-nowrap">PURCHASE VALUE</th>
                          <th className="text-end text-nowrap">SGST</th>
                          <th className="text-end text-nowrap">CGST</th>
                          <th className="text-end text-nowrap">TOTAL TAX</th>
                          <th className="text-end text-nowrap">GOLD (GM)</th>
                          <th className="text-end text-nowrap">SILVER (GM)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledgerRows.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.name}</td>
                            <td>{row.phone}</td>
                            <td>{row.purchase_no}</td>
                            <td>{row.purchase_date}</td>
                            <td className="text-end">
                              {formatCurrency(row.purchase_value)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(row.taxable_value)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(row.sgst)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(row.cgst)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(row.total_tax)}
                            </td>
                            <td className="text-end">{formatWeight(row.gold_gm)}</td>
                            <td className="text-end">{formatWeight(row.silver_gm)}</td>
                          </tr>
                        ))}
                        {/* Summary Row */}
                        {summaryData && (
                          <tr className="ledger-report-summary fw-bold" style={{ backgroundColor: "#f0e6d2" }}>
                            <td colSpan="4" className="ps-3">TOTAL</td>
                            <td className="text-end">
                              {formatCurrency(summaryData.purchase_value)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.taxable_value)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.sgst)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.cgst)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.total_tax)}
                            </td>
                            <td className="text-end">
                              {formatWeight(summaryData.gold_gm)}
                            </td>
                            <td className="text-end">
                              {formatWeight(summaryData.silver_gm)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {entireReport && totalCount > 0 && (
              <Pagination
                totalItems={totalCount}
                hasMore={hasMorePages}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[10, 20, 30, 50, 100]}
              />
            )}
          </div>
        </div>
      </div>

      {/* Print Loading Modal */}
      <Modal
        show={printLoading}
        backdrop="static"
        keyboard={false}
        centered
        size="md"
      >
        <Modal.Body className="text-center p-4">
          <div
            className="spinner-border text-primary mb-3"
            style={{ width: "3rem", height: "3rem" }}
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="mb-2">Preparing URD Ledger PDF</h5>
          <p className="text-muted mb-0" style={{ fontSize: "0.95rem" }}>
            This may take a few minutes. Thank you for your patience and understanding.
          </p>
        </Modal.Body>
      </Modal>

      {/* PDF Modal */}
      <Modal
        show={showPDFModal}
        onHide={() => {
          setShowPDFModal(false);
          if (pdfUrlRef.current) {
            URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = null;
          }
          setPdfBlobUrl(null);
        }}
        size="xl"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>URD Ledger PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh", padding: 0 }}>
          {pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              title="URD Ledger PDF"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default UrdLedger;
